import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampaigns, saveCampaign, deleteCampaign, updateCampaign, fetchSubSources } from "../../redux/slices/campaignSlice";
import { fetchLeadPartners } from "../../redux/slices/costPerLeadSlice";
import {
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Pagination,
    Spinner,
    Input,
    Select,
    SelectItem,
    Chip,
    Tooltip,
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    useDisclosure,
} from "@heroui/react";
import { SearchIcon } from "../../utils/svgIcons";
import useDebounce from "../../utils/useDebounce";
import { BsPlusCircle } from "react-icons/bs";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdEdit, MdDeleteOutline } from "react-icons/md";


const pageLimitsNumber = [
    { value: "10", label: "10", key: "10" },
    { value: "20", label: "20", key: "20" },
    { value: "50", label: "50", key: "50" },
    { value: "100", label: "100", key: "100" },
];

const formatNumberValue = (number) => {
    if (!number && number !== 0) return "N/A";
    return new Intl.NumberFormat("en-IN").format(number);
};

const columns = [
    { uid: "no", name: "No" },
    { uid: "name", name: "Campaign Name" },
    { uid: "subSources", name: "Sub Sources" },
    { uid: "oem", name: "OEM" },
    { uid: "actions", name: "Actions" },
];

/* ──────────────────────────────────────
   Custom Multi‑Select (sub sources)
   ────────────────────────────────────── */
const CustomMultiSelect = ({ label, placeholder, options, selectedKeys, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

    const toggleSelection = (key) => {
        if (selectedKeys.includes(key)) {
            onChange(selectedKeys.filter(k => k !== key));
        } else {
            onChange([...selectedKeys, key]);
        }
    };

    const removeSelection = (key) => {
        onChange(selectedKeys.filter(k => k !== key));
    };

    return (
        <div className="flex flex-col gap-1.5 w-full relative" ref={wrapperRef}>
            <label className="text-sm font-medium text-default-700">{label}</label>
            <div
                className={`min-h-[40px] w-full border ${isOpen ? 'border-success ring-1 ring-success' : 'border-default-200'} rounded-xl bg-default-100 hover:bg-default-200 transition-colors px-3 py-2 flex flex-wrap gap-2 items-center cursor-text relative`}
                onClick={() => setIsOpen(true)}
            >
                {selectedKeys.map(key => (
                    <Chip key={key} onClose={() => removeSelection(key)} size="sm" color="success" variant="flat">
                        {key}
                    </Chip>
                ))}
                <input
                    type="text"
                    className="flex-1 bg-transparent outline-none text-sm min-w-[120px] text-default-700 placeholder:text-default-500"
                    placeholder={selectedKeys.length === 0 ? placeholder : ""}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                />
            </div>

            {isOpen && (
                <div className="absolute gap-2 top-full left-0 w-full mt-2 max-h-48 overflow-y-auto bg-white border border-default-200 rounded-xl shadow-lg z-50 p-1">
                    {filteredOptions.length > 0 ? filteredOptions.map(opt => {
                        const isSelected = selectedKeys.includes(opt);
                        return (
                            <div
                                key={opt}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSelection(opt);
                                }}
                                className={`px-3 py-2 text-sm rounded-lg cursor-pointer flex justify-between items-center transition-colors ${isSelected ? 'bg-success/10 text-success font-medium' : 'hover:bg-default-100 text-default-700'}`}
                            >
                                {opt}
                                {isSelected && <span className="font-bold text-lg">✓</span>}
                            </div>
                        );
                    }) : (
                        <div className="px-3 py-2 text-sm text-default-500 text-center">No options found</div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ──────────────────────────────────────
   Main Campaign Page
   ────────────────────────────────────── */
const Campaigns = () => {
    const dispatch = useDispatch();
    const { campaigns, totalDocs, totalPages, status, error, subSources: subSourceList } = useSelector((state) => state.campaigns);
    const { leadPartners: oemList } = useSelector((state) => state.leadPartners);

    const [page, setPage] = useState(1);
    const [pageLimit, setPageLimit] = useState("10");
    const [searchInput, setSearchInput] = useState("");
    const debouncedSearch = useDebounce(searchInput, 500);

    // Modal states
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    // Form states
    const [campaignName, setCampaignName] = useState("");
    const [selectedSubSources, setSelectedSubSources] = useState([]);
    const [selectedOem, setSelectedOem] = useState("");
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [deletingCampaign, setDeletingCampaign] = useState(null);

    // Fetch OEMs and Sub Sources on mount
    useEffect(() => {
        dispatch(fetchLeadPartners());
        dispatch(fetchSubSources());
    }, [dispatch]);

    // Fetch campaigns on page / limit / search change
    useEffect(() => {
        dispatch(fetchCampaigns({ page, limit: parseInt(pageLimit, 10), search: debouncedSearch }));
    }, [dispatch, page, pageLimit, debouncedSearch]);

    const refetchCampaigns = useCallback(() => {
        dispatch(fetchCampaigns({ page, limit: parseInt(pageLimit, 10), search: debouncedSearch }));
    }, [dispatch, page, pageLimit, debouncedSearch]);

    /* ── Create ── */
    const resetForm = () => {
        setCampaignName("");
        setSelectedSubSources([]);
        setSelectedOem("");
    };

    const handleCreate = async () => {
        if (!campaignName.trim() || !selectedOem) return;
        try {
            await dispatch(saveCampaign({ name: campaignName.trim(), subSources: selectedSubSources, oem: selectedOem })).unwrap();
            resetForm();
            onCreateClose();
            refetchCampaigns();
        } catch (err) {
            console.error("Create failed:", err);
        }
    };

    /* ── Edit ── */
    const openEdit = (campaign) => {
        setEditingCampaign(campaign);
        setCampaignName(campaign.name || "");
        setSelectedSubSources(campaign.subSources || []);
        setSelectedOem(campaign.oem?._id || campaign.oem || "");
        onEditOpen();
    };

    const handleUpdate = async () => {
        if (!campaignName.trim() || !selectedOem || !editingCampaign) return;
        try {
            await dispatch(updateCampaign({
                campaignId: editingCampaign._id,
                campaignData: { name: campaignName.trim(), subSources: selectedSubSources, oem: selectedOem }
            })).unwrap();
            resetForm();
            setEditingCampaign(null);
            onEditClose();
            refetchCampaigns();
        } catch (err) {
            console.error("Update failed:", err);
        }
    };

    /* ── Delete ── */
    const openDelete = (campaign) => {
        setDeletingCampaign(campaign);
        onDeleteOpen();
    };

    const handleDelete = async () => {
        if (!deletingCampaign) return;
        try {
            await dispatch(deleteCampaign(deletingCampaign._id)).unwrap();
            setDeletingCampaign(null);
            onDeleteClose();
            refetchCampaigns();
        } catch (err) {
            console.error("Delete failed:", err);
        }
    };

    /* ── Cell Renderer ── */
    const renderCell = (row, columnKey, index) => {
        switch (columnKey) {
            case "no":
                return (
                    <p className="text-black text-medium font-bold">
                        {(page - 1) * parseInt(pageLimit, 10) + (index + 1)}
                    </p>
                );
            case "subSources": {
                const subSources = row.subSources || [];
                if (subSources.length === 0) return "-";

                const limit = 3;
                const displaySources = subSources.slice(0, limit);
                const remaining = subSources.length - limit;

                return (
                    <div className="flex flex-wrap gap-3 max-w-[700px]">
                        {displaySources.map((source, i) => (
                            <div
                                key={i}
                                className="bg-success inline-block text-white text-center rounded-global shadow-md text-[14px] py-1 px-3"
                            >
                                {source.trim()}
                            </div>
                        ))}
                        {remaining > 0 && (
                            <Tooltip
                                content={
                                    <div className="flex flex-col gap-2 px-1 py-1">
                                        {subSources.slice(limit).map((s, i) => (
                                            <span key={i}>{s.trim()}</span>
                                        ))}
                                    </div>
                                }
                                placement="bottom"
                            >
                                <div className="bg-white inline-block text-success border border-success text-center rounded-global text-[14px] py-1 px-3">
                                    +{remaining} more
                                </div>
                            </Tooltip>
                        )}
                    </div>
                );
            }
            case "oem":
                return row.oem?.title || "-";
            case "actions":
                return (
                    <div className="flex items-center justify-center gap-2">
                        <Tooltip content="Edit">
                            <Button isIconOnly size="md" variant="light" aria-label="Edit" onPress={() => openEdit(row)}>
                                <MdEdit size={20} className="text-default-600" />
                            </Button>
                        </Tooltip>
                        <Tooltip color="danger" content="Delete">
                            <Button isIconOnly size="md" variant="light" aria-label="Delete" onPress={() => openDelete(row)}>
                                <MdDeleteOutline size={20} className="text-danger" />
                            </Button>
                        </Tooltip>
                    </div>
                );
            default: {
                const value = row[columnKey];
                return value != null ? String(value) : "-";
            }
        }
    };

    const isLoading = status.fetch === "loading";
    const isSaving = status.save === "loading";
    const isUpdating = status.update === "loading";
    const isDeleting = status.delete === "loading";

    const handlePageChange = (selectedPage) => setPage(selectedPage);

    /* ── Campaign Form (shared between Create & Edit modals) ── */
    const renderCampaignForm = () => (
        <div className="flex flex-col gap-4">
            <Input
                labelPlacement="outside"
                type="text"
                label="Campaign Name"
                placeholder="Enter campaign name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                isRequired
                color="success"
                variant="bordered"
            />

            <Select
                label="OEM"
                labelPlacement="outside"
                placeholder="Select OEM"
                selectedKeys={selectedOem ? [selectedOem] : []}
                onSelectionChange={(keys) => setSelectedOem(Array.from(keys)[0] || "")}
                isRequired
                color="success"
                variant="bordered"
            >
                {oemList.map((oem) => (
                    <SelectItem key={oem._id} value={oem._id}>
                        {oem.title}
                    </SelectItem>
                ))}
            </Select>

            <CustomMultiSelect
                label="Select Sub Sources"
                placeholder="Search and select sub sources..."
                options={subSourceList}
                selectedKeys={selectedSubSources}
                onChange={setSelectedSubSources}
            />
        </div>
    );

    return (
        <div className="inline-flex overflow-hidden max-w-full w-full h-full rounded-global shadow-global">
            <div className="bg-white rounded-global shadow-global transition-width duration-300 h-full flex-[100] max-w-full w-full">
                <div className="grid-layout h-full">
                    {/* ── Header ── */}
                    <div className="w-full gap-4 lead-view-header flex flex-wrap items-center justify-between border-b border-default-200 py-4 px-6">
                        <div className="flex items-center 2xl:gap-5 gap-3 w-full justify-between">
                            <div className="total-leads-documents flex items-center whitespace-nowrap gap-1">
                                <span className="text-medium text-success">Total Campaigns :</span>
                                <span className="text-large font-bold">{formatNumberValue(totalDocs)}</span>
                            </div>
                            <div className="flex flex-row gap-4 ">
                                <div className="flex items-center gap-2 border border-default-200 rounded-[13px] hover:bg-gray-100">
                                    <Input
                                        classNames={{
                                            base: "max-w-full sm:max-w-[350px] h-10",
                                            inputWrapper: "h-full font-normal text-default-500 bg-transparent rounded-[13px]",
                                        }}
                                        onChange={(e) => {
                                            setSearchInput(e.target.value);
                                            setPage(1);
                                        }}
                                        value={searchInput}
                                        placeholder="Type to search..."
                                        size="sm"
                                        startContent={<SearchIcon size={18} />}
                                        type="text"
                                    />
                                </div>
                                <Button
                                    variant="flat"
                                    onClick={() => {
                                        resetForm();
                                        onCreateOpen();
                                    }}
                                    className="bg-success text-white"
                                    startContent={<BsPlusCircle size={18} />}
                                >
                                    Add Campaign
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* ── Table Content ── */}
                    <div className="leadsData overflow-x-auto h-[calc(100%-72px)] flex flex-col">
                        {isLoading ? (
                            <div className="w-full h-full flex items-center justify-center gap-2">
                                <Spinner color="success" />
                                <p className="!text-success text-3xl">Loading...</p>
                            </div>
                        ) : error ? (
                            <div className="text-center h-full flex items-center justify-center text-xl font-medium text-red-500">
                                Error: {error}
                            </div>
                        ) : campaigns && campaigns.length > 0 ? (
                            <div className="shadow-none px-4 py-6 rounded-outSideglobal w-full overflow-x-auto default-scrollbar h-full overflow-hidden flex flex-col">
                                <div className="overflow-x-auto overflow-y-auto h-[calc(100%-68px)] rounded-outSideglobal shadow-global default-scrollbar !bg-[rgb(229_243_251_/_var(--tw-bg-opacity,_1))] p-5 !pt-7">
                                    <Table
                                        aria-label="Campaigns table"
                                        removeWrapper
                                        classNames={{
                                            th: "bg-transparent text-default-600 font-semibold text-sm border-none",
                                            td: "border-none !py-[12px] !h-[58px]",
                                            tr: "border-none",
                                        }}
                                    >
                                        <TableHeader columns={columns}>
                                            {(column) => (
                                                <TableColumn key={column.uid} align={column.uid === "actions" ? "center" : "start"}>
                                                    {column.name}
                                                </TableColumn>
                                            )}
                                        </TableHeader>
                                        <TableBody items={campaigns}>
                                            {(item) => (
                                                <TableRow key={item._id || item.id}>
                                                    {(columnKey) => (
                                                        <TableCell>
                                                            {renderCell(item, columnKey, campaigns.indexOf(item))}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* ── Pagination ── */}
                                <div className="pagination-wrapper w-full flex items-center justify-between mt-4">
                                    <div className="flex items-center justify-center w-[250px] gap-1">
                                        <p>Show</p>
                                        <Select
                                            className="max-w-[90px] bg-black! border-gray-100 rounded-[13px]"
                                            placeholder="Page size"
                                            color="custom-bg"
                                            variant="bordered"
                                            defaultSelectedKeys={["10"]}
                                            selectedKeys={[pageLimit]}
                                            onSelectionChange={(keys) => {
                                                const newLimit = Array.from(keys)[0];
                                                setPageLimit(newLimit);
                                                setPage(1);
                                            }}
                                        >
                                            {pageLimitsNumber.map((item) => (
                                                <SelectItem key={item.key} value={item.value}>
                                                    {item.label}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                        <p>Entries</p>
                                    </div>
                                    <div className="flex w-full justify-end mt-2">
                                        <Pagination
                                            key={page}
                                            color="success"
                                            classNames={{
                                                wrapper: "",
                                                item: "",
                                                cursor: "bg-success text-white",
                                            }}
                                            size={(page > 100 || page > '100') ? 'lg' : 'md'}
                                            isCompact
                                            showControls
                                            total={totalPages}
                                            page={page}
                                            onChange={(p) => handlePageChange(p)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center h-full flex items-center justify-center text-3xl font-bold">No records found.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════
                Create Campaign Modal
               ══════════════════════════════════ */}
            <Modal
                isDismissable={false}
                isKeyboardDismissDisabled={true}
                isOpen={isCreateOpen}
                onOpenChange={(open) => { if (!open) onCreateClose(); }}
                size="lg"
            >
                <ModalContent className="overflow-visible">
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">Add Campaign</ModalHeader>
                            <ModalBody>
                                {renderCampaignForm()}
                            </ModalBody>
                            <ModalFooter>
                                <Button color="danger" variant="light" onPress={onClose}>
                                    Close
                                </Button>
                                <Button
                                    color="success"
                                    className="text-white"
                                    onPress={handleCreate}
                                    isLoading={isSaving}
                                    isDisabled={!campaignName.trim() || !selectedOem}
                                >
                                    Create
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* ══════════════════════════════════
                Edit Campaign Modal
               ══════════════════════════════════ */}
            <Modal
                isDismissable={false}
                isKeyboardDismissDisabled={true}
                isOpen={isEditOpen}
                onOpenChange={(open) => { if (!open) { onEditClose(); setEditingCampaign(null); resetForm(); } }}
                size="lg"
            >
                <ModalContent className="overflow-visible">
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">Edit Campaign</ModalHeader>
                            <ModalBody>
                                {renderCampaignForm()}
                            </ModalBody>
                            <ModalFooter>
                                <Button color="danger" variant="light" onPress={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    color="success"
                                    className="text-white"
                                    onPress={handleUpdate}
                                    isLoading={isUpdating}
                                    isDisabled={!campaignName.trim() || !selectedOem}
                                >
                                    Update
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
            <Modal
                isDismissable={false}
                isKeyboardDismissDisabled={true}
                isOpen={isDeleteOpen}
                onOpenChange={(open) => { if (!open) { onDeleteClose(); setDeletingCampaign(null); } }}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">Delete Campaign</ModalHeader>
                            <ModalBody>
                                <p>
                                    Are you sure you want to delete{" "}
                                    <strong className="text-danger">"{deletingCampaign?.name}"</strong>?
                                    This action cannot be undone.
                                </p>
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="light" onPress={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    color="danger"
                                    onPress={handleDelete}
                                    isLoading={isDeleting}
                                >
                                    Delete
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};

export default Campaigns;